import { Component, EventEmitter, HostBinding, HostListener, Input, OnChanges, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { TableOptionShared } from '../../../models/interface/actionValueProerty.interface';
import { MenuItemShared } from '../../../models/interface/menuItem.interface';
import { MuFieldPositionEnum } from '../../../models/interface/muFieldPosition.enum';
import { StatusShared } from '../../../models/interface/status.interface';
import { Actions } from '../../../types/actions.type';
import { TableEditingActionsTypes } from '../models/tableEditingActionsTypes.enum';
import { TableRowEditor } from '../mu-table/mu-table.component';

enum OptionMode {
  ROW_EDITING,
  ROW_EDITING_WAITING,
  NORMAL,
}

@Component({
  selector: 'app-mu-table-options',
  templateUrl: './mu-table-options.component.html',
  styleUrls: ['./mu-table-options.component.sass'],
})
export class MuTableOptionsComponent<ActionList> implements OnInit, OnChanges {
  @HostBinding('class') className: string;
  @Input() columnSize: string;
  @Input() isMobile: boolean;
  @Input() data: { [key: string]: any } = {};
  @Input() statusId: string;
  @Input() statusList: StatusShared;
  @Input() permissionList: { [key: string]: ActionList };
  @Input() tableOptionList: TableOptionShared[] = [];
  @Input() tableRowEditor: TableRowEditor<unknown>;
  @Input() customClass: string;
  @Input() showAlways: boolean;
  @Input() checkPermissions = true;
  @Input() actions = new Actions<any, any>();
  @Input() menuPosition: MuFieldPositionEnum;
  @Output() eventAction = new EventEmitter();
  optionMode = OptionMode;
  tableEditingActionsTypes = TableEditingActionsTypes;
  baseTableOptionList: TableOptionShared[] = [];
  menuActionList: MenuItemShared[] = [];

  constructor(private translateService: TranslateService, private router: Router) {
    /** */
  }

  ngOnInit(): void {
    this.className += this.columnSize !== '' ? ` p-col-align-center p-col-${this.columnSize}` : '';
    this.className += ` ${this.customClass}`;
  }

  ngOnChanges(changes: any): void {
    const isPermissionListNotEmpty = Object.entries(this.permissionList).length !== 0 && this.permissionList.constructor === Object;

    if (this.baseTableOptionList.length === 0 && this.tableOptionList) {
      this.baseTableOptionList = this.tableOptionList;
    }
    if (this.actions && this.tableOptionList && this.menuActionList.length < 1 && isPermissionListNotEmpty) {
      this.setMenuActionList();
    }
    if (
      this.tableOptionList &&
      changes.tableOptionList &&
      (changes.tableOptionList.currentValue || []).length !== (changes.tableOptionList.previousValue || []).length
    ) {
      this.tableOptionList = this.baseTableOptionList.filter(x => !x.group);
    }
  }

  /**
   * is mobile screen
   */
  get isMobileScreen(): boolean {
    return window.innerWidth < 1023;
  }

  /**
   * method to fire an action from actions component
   *
   * @param {string} actionType Action to fire
   */
  _eventAction(option: TableOptionShared): void | boolean {
    if (
      this.checkPermissions &&
      (!this.permissionList ||
        (this.permissionList &&
          (!this.permissionList[this.data.id][option.actionType] ||
            (this.permissionList[this.data.id][option.actionType] !== undefined &&
              this.permissionList[this.data.id][option.actionType].value === false))))
    ) {
      return environment.guardClause;
    }
    if (option.actionType === TableEditingActionsTypes.START_ROW_EDITING && this.tableRowEditor) {
      this.tableRowEditor.edit();
      return environment.guardClause;
    }
    if (option.routerLink) {
      this.router.navigate([option.routerLink, this.data[option.routerLinkKey]]);
      return environment.guardClause;
    }
    if (this.actions) {
      this.actions.emit(option.actionType, this.data);
    }
    this.eventAction.emit({ type: option.actionType, value: this.data });
  }

  /**
   * row editing action
   *
   * @param {TableEditingActionsTypes} actionType
   */
  _rowEditingAction(actionType: TableEditingActionsTypes): void {
    this.tableRowEditor.beginEndEditing();
    if (this.actions) {
      this.actions.emit(actionType, this.tableRowEditor);
    }
    this.eventAction.emit({ type: actionType, value: this.tableRowEditor });
  }

  /**
   * get option mode
   *
   * @returns {OptionMode} response
   */
  get optionsModeSwitch(): OptionMode {
    if (this.tableRowEditor && this.tableRowEditor.endEditing) {
      return OptionMode.ROW_EDITING_WAITING;
    }
    if (this.tableRowEditor && this.tableRowEditor.editing) {
      return OptionMode.ROW_EDITING;
    }
    return OptionMode.NORMAL;
  }

  /**
   * check if option is disabled
   *
   * @param {TableOptionShared} option
   * @returns {boolean} response
   */
  isDisabled(option: TableOptionShared): boolean {
    return (
      this.checkPermissions &&
      (!this.permissionList ||
        !this.data ||
        !this.permissionList[this.data.id] ||
        this.permissionList[this.data.id][option.actionType] === false ||
        (this.permissionList[this.data.id][option.actionType] !== undefined &&
          this.permissionList[this.data.id][option.actionType].value !== undefined &&
          this.permissionList[this.data.id][option.actionType].value === false))
    );
  }

  /**
   * chck if option is hidden
   *
   * @param {TableOptionShared} option
   * @returns {boolean} response
   */
  isHidden(option: TableOptionShared): boolean {
    return (
      this.data &&
      this.permissionList &&
      this.permissionList[this.data.id] &&
      this.permissionList[this.data.id][option.actionType] &&
      this.permissionList[this.data.id][option.actionType].hiden !== undefined &&
      this.permissionList[this.data.id][option.actionType].hiden === true
    );
  }

  /**
   * set menu action list
   */
  @HostListener('window:resize')
  setMenuActionList(): void {
    this.menuActionList = this.baseTableOptionList
      .filter(option => option.group)
      .map(option => {
        return {
          label: this.translateService.instant(`tooltip.${option.actionType}`),
          icon: `fas fa-${option.icon}`,
          disabled: this.isDisabled(option),
          visible: option.visible,
          routerLink: option.routerLink,
          routerLinkKey: option.routerLinkKey,
          command: () => {
            if (option.disabled) {
              return environment.guardClause;
            }
            if (option.routerLink) {
              return this.router.navigate([option.routerLink, this.data[option.routerLinkKey]]);
            }
            this.actions.emit(option.actionType, this.data);
          },
        };
      });
  }
}
